import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import SurveyServices from "../../../services/inspection-overview/site-operations/survey.services";

export const EMPTY_SURVEY_OVERVIEW_SUMMARY = {
  total: 0,
  completed: 0,
  remaining: 0,
};

export function buildSurveyOverviewSummaryCacheKey(
  cameraSiteId: string,
  surveyDate: string,
  siteName: string
): string {
  return `${cameraSiteId}|${surveyDate}|${siteName}`;
}

function normalizeSurveyOverviewSummary(raw: unknown) {
  if (raw && typeof raw === "object" && Object.keys(raw as object).length > 0) {
    return raw as Record<string, unknown>;
  }
  return { ...EMPTY_SURVEY_OVERVIEW_SUMMARY };
}

type OverviewPayload = {
  cacheKey: string;
  data: Record<string, unknown>;
};

type SurveyOverviewInflight = Promise<OverviewPayload>;

const surveyOverviewSummaryInflight = new Map<string, SurveyOverviewInflight>();

export type EnsureSurveyOverviewSummaryArgs = {
  cameraSiteId: string;
  surveyDate: string;
  siteName: string;
};

/**
 * Loads survey overview summary for the given site/date/name.
 * Skips the network when the same key is already cached in Redux.
 * Dedupes concurrent in-flight requests for the same key.
 */
export const ensureSurveyOverviewSummary = createAsyncThunk(
  "SurveyDetails/ensureSurveyOverviewSummary",
  async (
    { cameraSiteId, surveyDate, siteName }: EnsureSurveyOverviewSummaryArgs,
    { getState }
  ) => {
    const cacheKey = buildSurveyOverviewSummaryCacheKey(
      cameraSiteId,
      surveyDate,
      siteName
    );

    const surveyDetails = (
      getState() as {
        SurveyDetails: {
          surveyOverviewSummaryCacheKey: string | null;
          surveySummary: unknown;
        };
      }
    ).SurveyDetails;

    if (surveyDetails.surveyOverviewSummaryCacheKey === cacheKey) {
      const cached = surveyDetails.surveySummary;
      const data =
        cached &&
        typeof cached === "object" &&
        Object.keys(cached as object).length > 0
          ? (cached as Record<string, unknown>)
          : { ...EMPTY_SURVEY_OVERVIEW_SUMMARY };
      return { cacheKey, data };
    }

    let inflight = surveyOverviewSummaryInflight.get(cacheKey);
    if (!inflight) {
      inflight = (async (): Promise<OverviewPayload> => {
        try {
          const raw = await SurveyServices.getSurveyOverviewSummary(
            cameraSiteId,
            surveyDate,
            siteName
          );
          const data = normalizeSurveyOverviewSummary(raw);
          return { cacheKey, data };
        } catch (error) {
          console.error("Error fetching survey summary:", error);
          return {
            cacheKey,
            data: { ...EMPTY_SURVEY_OVERVIEW_SUMMARY },
          };
        } finally {
          surveyOverviewSummaryInflight.delete(cacheKey);
        }
      })();
      surveyOverviewSummaryInflight.set(cacheKey, inflight);
    }

    return inflight;
  }
);

const SurveyDetails = createSlice({
  name: "SurveyDetails",
  initialState: {
    surveyInfo: {},
    surveySummary: {} as Record<string, unknown>,
    surveyOverviewSummaryCacheKey: null as string | null,
    selectedSiteInfo: {
      name: "",
      code: "",
      cameraSiteId: "",
      lastSurveyDate: "",
      nextSurveyDate: "",
      latitude: "",
      longitude: "",
    },
    selectedCustomerInfo: {},
  },
  reducers: {
    SurveyInfoReducer: (state: any, action: any) => {
      state.surveyInfo = action.payload;
      return state;
    },
    SurveySummaryReducer: (state: any, action: any) => {
      state.surveySummary = action.payload;
      state.surveyOverviewSummaryCacheKey = null;
      return state;
    },
    SelectedSiteReducer: (state: any, action: any) => {
      state.selectedSiteInfo = action.payload;
      return state;
    },
    SelectedCustomerReducer: (state: any, action: any) => {
      state.selectedCustomerInfo = action.payload;
      return state;
    },
    invalidateSurveyOverviewSummaryCache: (state: any) => {
      state.surveyOverviewSummaryCacheKey = null;
      state.surveySummary = {};
      return state;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(ensureSurveyOverviewSummary.fulfilled, (state, action) => {
      state.surveyOverviewSummaryCacheKey = action.payload.cacheKey;
      state.surveySummary = action.payload.data;
    });
  },
});

export const {
  SurveyInfoReducer,
  SelectedSiteReducer,
  SurveySummaryReducer,
  SelectedCustomerReducer,
  invalidateSurveyOverviewSummaryCache,
} = SurveyDetails.actions;

export default SurveyDetails;
